//---------------------------------------------------------------------------------------------------------------------
/**
 * Given a single `Iterable`, returns an array of 2 iterables, mirroring the original one (which should not be used anymore).
 *
 * For example:
 *
 *     const gen = function* () { yield 1; yield 2; yield 3 }
 *
 *     const [ iterable1, iterable2 ] = split(gen())
 *     const [ iter1, iter2 ] = [
 *         iterable1[ Symbol.iterator ](),
 *         iterable2[ Symbol.iterator ]()
 *     ]
 *
 *     iter1.next() // 1
 *     iter2.next() // 1
 *     iter2.next() // 2
 *     iter2.next() // 3
 *     iter1.next() // 2
 *     iter1.next() // 3
 *     iter1.next() // done
 *     iter2.next() // done
 *
 * @param iterable
 */
export function split(iterable) {
    const gen1Pending = [];
    const gen2Pending = [];
    let iterator;
    const gen1 = function* () {
        if (!iterator)
            iterator = iterable[Symbol.iterator]();
        while (true) {
            if (gen1Pending.length) {
                yield* gen1Pending;
                gen1Pending.length = 0;
            }
            if (!iterator)
                break;
            const { value, done } = iterator.next();
            if (done) {
                iterator = null;
                iterable = null;
                break;
            }
            gen2Pending.push(value);
            yield value;
        }
    };
    const gen2 = function* () {
        if (!iterator)
            iterator = iterable[Symbol.iterator]();
        while (true) {
            if (gen2Pending.length) {
                yield* gen2Pending;
                gen2Pending.length = 0;
            }
            if (!iterator)
                break;
            const { value, done } = iterator.next();
            if (done) {
                iterator = null;
                iterable = null;
                break;
            }
            gen1Pending.push(value);
            yield value;
        }
    };
    return [gen1(), gen2()];
}
//---------------------------------------------------------------------------------------------------------------------
export function* inBatchesBySize(iterator, batchSize) {
    if (batchSize < 0)
        throw new Error("Batch size needs to a natural number");
    batchSize = batchSize | 0;
    const runningBatch = [];
    for (const el of iterator) {
        if (runningBatch.length === batchSize) {
            yield runningBatch;
            runningBatch.length = 0;
        }
        runningBatch.push(el);
    }
    if (runningBatch.length > 0)
        yield runningBatch;
}
//---------------------------------------------------------------------------------------------------------------------
export function* filter(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        if (func(el, i++))
            yield el;
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* drop(iterator, howMany) {
    let i = 0;
    for (const el of iterator) {
        if (++i > howMany)
            yield el;
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function every(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        if (!func(el, i++))
            return false;
    }
    return true;
}
//---------------------------------------------------------------------------------------------------------------------
export function some(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        if (func(el, i++))
            return true;
    }
    return false;
}
//---------------------------------------------------------------------------------------------------------------------
export function* map(iterator, func) {
    let i = 0;
    for (const el of iterator)
        yield func(el, i++);
}
//---------------------------------------------------------------------------------------------------------------------
export function reduce(iterator, func, initialAcc) {
    let i = 0;
    let acc = initialAcc;
    for (const el of iterator) {
        acc = func(acc, el, i++);
    }
    return acc;
}
//---------------------------------------------------------------------------------------------------------------------
export function* uniqueOnly(iterator) {
    const seen = new Set();
    for (const el of iterator) {
        if (!seen.has(el)) {
            seen.add(el);
            yield el;
        }
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* uniqueOnlyBy(iterator, func) {
    const seen = new Set();
    for (const el of iterator) {
        const uniqueBy = func(el);
        if (!seen.has(uniqueBy)) {
            seen.add(uniqueBy);
            yield el;
        }
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* reverse(iterator) {
    const all = Array.from(iterator);
    for (let i = all.length - 1; i >= 0; i--)
        yield all[i];
}
//---------------------------------------------------------------------------------------------------------------------
export function* takeWhile(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        if (func(el, i++))
            yield el;
        else
            return;
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* takeUntilIncluding(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        yield el;
        if (func(el, i++))
            return;
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* takeUntilExcluding(iterator, func) {
    let i = 0;
    for (const el of iterator) {
        if (func(el, i++))
            return;
        yield el;
    }
}
//---------------------------------------------------------------------------------------------------------------------
export function* concat(...iterators) {
    for (let i = 0; i < iterators.length; i++)
        yield* iterators[i];
}
//---------------------------------------------------------------------------------------------------------------------
export function* concatIterable(iteratorsProducer) {
    for (const iterator of iteratorsProducer)
        yield* iterator;
}
//---------------------------------------------------------------------------------------------------------------------
// just a chained syntax sugar class
// note, that we either use a combination of `this.derive()` + this.iterable (which will clear the `this.iterable`)
// or, use just `this` as iterable, which will also clear the iterator
//
export class ChainedIteratorClass {
    constructor(iterable) {
        this.iterable = undefined;
        if (!iterable)
            throw new Error("Require an iterable instance for chaining");
        this.iterable = iterable;
    }
    derive(iterable) {
        this.iterable = undefined;
        return new ChainedIteratorClass(iterable);
    }
    copy() {
        const [iter1, iter2] = split(this.iterable);
        this.iterable = iter2;
        return new ChainedIteratorClass(iter1);
    }
    split() {
        const [iter1, iter2] = split(this.iterable);
        return [new ChainedIteratorClass(iter1), this.derive(iter2)];
    }
    inBatchesBySize(batchSize) {
        return this.derive(inBatchesBySize(this.iterable, batchSize));
    }
    filter(func) {
        return this.derive(filter(this.iterable, func));
    }
    drop(howMany) {
        return this.derive(drop(this.iterable, howMany));
    }
    map(func) {
        return this.derive(map(this.iterable, func));
    }
    reduce(func, initialAcc) {
        return reduce(this, func, initialAcc);
    }
    concat() {
        //@ts-ignore
        return this.derive(concatIterable(this.iterable));
    }
    uniqueOnly() {
        return this.derive(uniqueOnly(this.iterable));
    }
    uniqueOnlyBy(func) {
        return this.derive(uniqueOnlyBy(this.iterable, func));
    }
    every(func) {
        return every(this, func);
    }
    some(func) {
        return some(this, func);
    }
    takeWhile(func) {
        return this.derive(takeWhile(this.iterable, func));
    }
    *[Symbol.iterator]() {
        let iterable = this.iterable;
        if (!iterable)
            throw new Error("Chained iterator already exhausted or used to derive the new one");
        // practice shows, that cleaning up the iterable after yourself helps garbage collector a lot
        this.iterable = undefined;
        yield* iterable;
        // yes, we really want to avoid memory leaks
        iterable = undefined;
    }
    toArray() {
        return Array.from(this);
    }
    sort(order) {
        return Array.from(this).sort(order);
    }
    toSet() {
        return new Set(this);
    }
    toMap() {
        //@ts-ignore
        return new Map(this);
    }
    // toMap<K, V> () : T extends [ K, V ] ? Map<K, V> : never  {
    //     return new Map<K, V>(this.iterable as (T extends [ K, V ] ? Iterable<T> : never)) as (T extends [ K, V ] ? Map<K, V> : never)
    // }
    flush() {
        for (const element of this) { }
    }
    memoize() {
        return new MemoizedIteratorClass(this);
    }
}
export const ChainedIterator = (iterator) => new ChainedIteratorClass(iterator);
export const CI = ChainedIterator;
//---------------------------------------------------------------------------------------------------------------------
export class MemoizedIteratorClass extends ChainedIteratorClass {
    constructor() {
        super(...arguments);
        this.elements = [];
        this.$iterator = undefined;
    }
    set iterable(iterable) {
        this.$iterable = iterable;
    }
    get iterable() {
        return this;
    }
    derive(iterable) {
        return new ChainedIteratorClass(iterable);
    }
    *[Symbol.iterator]() {
        const elements = this.elements;
        if (this.$iterable) {
            if (!this.$iterator)
                this.$iterator = this.$iterable[Symbol.iterator]();
            let iterator = this.$iterator;
            let alreadyConsumed = elements.length;
            // yield the 1st batch "efficiently"
            if (alreadyConsumed > 0)
                yield* elements;
            while (true) {
                if (elements.length > alreadyConsumed) {
                    // wonder if `yield* elements.slice(alreadyConsumed)` is more performant or not
                    for (let i = alreadyConsumed; i < elements.length; i++)
                        yield elements[i];
                    alreadyConsumed = elements.length;
                }
                if (!iterator)
                    break;
                const { value, done } = iterator.next();
                if (done) {
                    iterator = this.$iterator = null;
                    this.$iterable = null;
                }
                else {
                    elements.push(value);
                    alreadyConsumed++;
                    yield value;
                }
            }
        }
        else {
            yield* elements;
        }
    }
}
export const MemoizedIterator = (iterator) => new MemoizedIteratorClass(iterator);
export const MI = MemoizedIterator;
